import React, { ChangeEvent, useCallback, useEffect, useMemo } from 'react'
import { Controller } from 'react-hook-form'
import classNames from 'classnames'
import get from 'lodash/get'
import isEqual from 'lodash/isEqual'
import sortBy from 'lodash/sortBy'
import unionBy from 'lodash/unionBy'
import ControlledHiddenInput from 'transfix-form/components/ControlledHiddenInput'
import ControlledSelect from 'transfix-form/components/ControlledSelect'
import InfoIcon from 'transfix-icons/build/InfoIcon'
import { Checkbox } from 'transfix-ui/components/Checkbox'
import { INLINE_MESSAGE_VARIANTS, InlineMessage } from 'transfix-ui/components/InlineMessage'
import { Tooltip } from 'transfix-ui/components/Tooltip'
import { Box, makeStyles } from 'transfix-ui/core'
import { brandColors } from 'transfix-ui/theme'
import { useBreakpointContext } from 'utils/context/BreakpointContext'
import {
  CarrierListItem,
  CarrierListItemEmpty,
} from 'apps/fleet-planner/components/team/form/CarrierListItem'
import { useTeamMemberContext } from 'apps/fleet-planner/context/TeamMemberContext'
import { useTeamMemberFormContext } from 'apps/fleet-planner/context/TeamMemberFormContext'
import { MotorCarrierDetailsSchemaType } from 'apps/fleet-planner/schema/AddTeamMemberSchema'
import {
  IMotorCarrierDetail,
  TEAM_MEMBER_ROLE_TYPES,
  TEAM_MEMBER_USER_TYPES,
} from 'apps/fleet-planner/types'
import {
  ADMIN_ROLE_OPTIONS,
  DRIVER_ROLE_OPTIONS,
  ROLE_OPTIONS,
  USER_TYPE_OPTIONS,
} from './constants'

const carrierAccessListStyles = makeStyles(({ spacing }) => ({
  header: {
    borderBottom: `1px solid ${brandColors.coolGray4}`,
    fontWeight: 'bold',
  },
  disabled: {
    color: brandColors.coolGray5,
    '& > label': {
      color: `${brandColors.coolGray5} !important`,
    },
  },
  tooltipContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    '& > span': {
      height: 24,
      display: 'grid',
      placeItems: 'center',
    },
  },
  checkboxContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: spacing(1),
    height: spacing(4),
  },
  row: {
    display: 'grid',
    gridTemplateColumns: '2fr minmax(140px, 1fr) minmax(140px, 1fr)',
    placeItems: 'start stretch',
    alignItems: 'stretch',
    gap: spacing(2),
    padding: spacing(1),
    '& > .deprecated-checkbox > label': {
      color: brandColors.black,
    },
  },
  warning: {
    gridColumn: '1 / -1',
  },
  checked: {
    background: brandColors.skyBlue2,
  },
}))

export const CarrierAccessList = () => {
  const { isMobile } = useBreakpointContext()
  const classes = carrierAccessListStyles()
  const { currentTeamMember, selectedTeamMember } = useTeamMemberContext()
  const { control, errors, reset, setValue, watch } = useTeamMemberFormContext()
  const formFields = watch()

  // Form State Management

  const carrierErrors = get(errors, 'motorCarrierDetails.message', undefined)

  const currentUserCarriers = useMemo(
    () =>
      (currentTeamMember?.motorCarrierDetails || []).map(mcd => ({
        ...mcd,
        role: undefined,
        userType: undefined,
      })),
    [currentTeamMember?.motorCarrierDetails]
  )

  const isRoleEditable = useMemo(() => selectedTeamMember?.id !== currentTeamMember?.id, [
    currentTeamMember?.id,
    selectedTeamMember?.id,
  ])

  const initialCarriers = useMemo(() => selectedTeamMember?.motorCarrierDetails || [], [
    selectedTeamMember?.motorCarrierDetails,
  ])

  const availableCarriers = useMemo(() => formFields.motorCarrierDetails || [], [
    formFields.motorCarrierDetails,
  ])

  const checkedCarriers = useMemo(() => availableCarriers.filter(mcd => mcd.checked), [
    availableCarriers,
  ])

  const currentUserCanAdmin = useCallback(
    (mcd: IMotorCarrierDetail) =>
      currentTeamMember?.motorCarrierDetails?.some(
        cuc =>
          cuc.motorCarrierId === mcd.motorCarrierId && cuc.role === TEAM_MEMBER_ROLE_TYPES.ADMIN
      ),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentTeamMember?.motorCarrierDetails]
  )

  const allCarriersChecked = useMemo(
    () =>
      isEqual(
        availableCarriers.filter(mcd => currentUserCanAdmin(mcd)),
        checkedCarriers
      ),
    [availableCarriers, checkedCarriers, currentUserCanAdmin]
  )

  const carrierCheckboxAllHandler = (event: ChangeEvent<HTMLInputElement>) => {
    const isChecked = event.currentTarget.checked
    reset({
      ...formFields,
      motorCarrierDetails: availableCarriers.map(mcd => {
        const notAdmin = !currentUserCanAdmin(mcd)
        return {
          ...mcd,
          role: isChecked || notAdmin ? mcd.role : undefined,
          userType: isChecked || notAdmin ? mcd.userType : undefined,
          checked: notAdmin ? mcd.checked : isChecked,
        }
      }),
    })
  }

  const isCarrierChecked = (mcd: MotorCarrierDetailsSchemaType) =>
    checkedCarriers.some(checked => checked.motorCarrierId === mcd.motorCarrierId)

  const isUserTypeDriver = (mcd: MotorCarrierDetailsSchemaType) =>
    mcd.userType === TEAM_MEMBER_USER_TYPES.DRIVER

  // Form Initialization

  useEffect(
    () => {
      const motorCarrierDetails = sortBy(
        unionBy(initialCarriers, currentUserCarriers, 'motorCarrierId'),
        'legalName'
      ).map(mcd => ({
        ...mcd,
        checked: initialCarriers.some(selected => mcd.motorCarrierId === selected.motorCarrierId),
      }))
      reset({
        ...formFields,
        motorCarrierDetails,
      })
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  )

  // Form Elements

  const validationBannerElement = (
    <Box mt={4}>
      <InlineMessage message={carrierErrors} type={INLINE_MESSAGE_VARIANTS.WARNING} />
    </Box>
  )

  const headerRowClasses = classNames(classes.row, classes.header)
  const headerLabelClasses = classNames(classes.tooltipContainer, classes.disabled)
  const tableHeadingElements = (
    <div className={headerRowClasses}>
      <Checkbox label='Carrier' onChange={carrierCheckboxAllHandler} checked={allCarriersChecked} />
      <div className={headerLabelClasses}>
        <span>Role</span>
        <Tooltip
          position='top'
          type='dark'
          trigger={
            <span>
              <InfoIcon size='medium' color='coolGray5' verticalAlign='text-top' />
            </span>
          }>
          <h3>Dispatchers</h3>
          <ul>
            <li>Can view and manage all of a carrier’s loads</li>
            <li>Can only be assigned to loads as a Dispatcher</li>
          </ul>
          <h3>Drivers</h3>
          <ul>
            <li>Can only view or manage loads they are assigned to</li>
            <li>Can only be assigned to loads as a Driver</li>
          </ul>
          <h3>Owner Operators</h3>
          <ul>
            <li>Can view and manage all of a carrier’s loads</li>
            <li>Can be assigned to loads as a Dispatcher or Driver</li>
          </ul>
        </Tooltip>
      </div>
      <div className={headerLabelClasses}>
        <span>Permissions</span>
        <Tooltip
          position='top'
          type='dark'
          trigger={
            <span>
              <InfoIcon size='medium' color='coolGray5' verticalAlign='text-top' />
            </span>
          }>
          <h3>Admin Permissions</h3>
          <ul>
            <li>Can invite new team members to a carrier</li>
            <li>Can view all of their carrier’s information</li>
          </ul>
          <h3>Employee Permissions</h3>
          <ul>
            <li>Can’t invite new team members to a carrier</li>
            <li>Can view all of their carrier’s information</li>
          </ul>
        </Tooltip>
      </div>
    </div>
  )

  const rowElements = availableCarriers.map(mcd => {
    const canEdit = currentUserCanAdmin(mcd)
    const hasAccessBeenRemoved =
      !isCarrierChecked(mcd) &&
      initialCarriers.some(selected => mcd.motorCarrierId === selected.motorCarrierId)

    const idx = availableCarriers.indexOf(mcd)
    const carrierLabel =
      currentUserCarriers.find(userCarrier => userCarrier.motorCarrierId === mcd.motorCarrierId)
        ?.legalName || ''
    const rowClass = classNames(classes.row, {
      [classes.checked]: isCarrierChecked(mcd),
    })
    const checkboxContainerClasses = classes.checkboxContainer
    const disableableClass = classNames({}, { [classes.disabled]: !canEdit })

    return (
      <div key={idx} className={rowClass}>
        {hasAccessBeenRemoved && (
          <InlineMessage
            className={classes.warning}
            message={
              <div>
                <strong>Note: </strong>
                <span>
                  Your team member will no longer be able to view loads from this carrier and will
                  have to be re-added if you change your mind.
                </span>
              </div>
            }
            type={INLINE_MESSAGE_VARIANTS.WARNING}
          />
        )}

        <ControlledHiddenInput
          name={`motorCarrierDetails[${idx}].motorCarrierId`}
          defaultValue={mcd.motorCarrierId}
        />
        <ControlledHiddenInput
          name={`motorCarrierDetails[${idx}].legalName`}
          defaultValue={mcd.legalName}
        />
        <div className={checkboxContainerClasses}>
          <Controller
            control={control}
            defaultValue={false}
            name={`motorCarrierDetails[${idx}].checked`}
            render={({ onChange, value }) => {
              const changeHandler = (isChecked: boolean) => {
                onChange(isChecked)
              }
              const carrierCheckboxHandler = (event: React.FormEvent<HTMLInputElement>) => {
                const isChecked = event.currentTarget.checked
                if (!isChecked) {
                  setValue(`motorCarrierDetails[${idx}].role`, null)
                  setValue(`motorCarrierDetails[${idx}].userType`, null)
                }
                changeHandler(isChecked)
              }
              return (
                <Checkbox
                  id={String(mcd.motorCarrierId)}
                  className={disableableClass}
                  label={carrierLabel}
                  checked={value}
                  onChange={carrierCheckboxHandler}
                  disabled={!canEdit}
                />
              )
            }}
          />
          {!canEdit && (
            <div className={disableableClass}>
              <Tooltip
                position='right'
                type='dark'
                trigger={
                  <span>
                    <InfoIcon color='coolGray5' />
                  </span>
                }>
                You don&apos;t have editing permissions for this carrier.
              </Tooltip>
            </div>
          )}
        </div>
        <div>
          <ControlledSelect
            control={control}
            defaultValue={null}
            name={`motorCarrierDetails[${idx}].userType`}
            SelectProps={{
              disabled: !get(formFields, `motorCarrierDetails[${idx}].checked`) || !canEdit,
              options: [...USER_TYPE_OPTIONS],
              placeholder: 'Select an option',
              validationMessage: get(errors, `motorCarrierDetails[${idx}].userType.message`, ''),
              error: !!get(errors, `motorCarrierDetails[${idx}].userType.message`, ''),
            }}
            onChange={e => {
              const isTypeDriver = e.target.value === 'driver'
              const isRoleForAdmin = ADMIN_ROLE_OPTIONS.some(
                role => role.value === watch(`motorCarrierDetails[${idx}].role`)
              )
              const shouldRemoveRole = isTypeDriver && isRoleForAdmin
              if (shouldRemoveRole) {
                setValue(`motorCarrierDetails[${idx}].role`, null)
              }
            }}
          />
        </div>
        <div>
          <ControlledSelect
            control={control}
            defaultValue={null}
            name={`motorCarrierDetails[${idx}].role`}
            SelectProps={{
              disabled:
                !get(formFields, `motorCarrierDetails[${idx}].checked`) ||
                !isRoleEditable ||
                !canEdit,
              options: isUserTypeDriver(mcd) ? DRIVER_ROLE_OPTIONS : ROLE_OPTIONS,
              placeholder: 'Select an option',
              validationMessage: get(errors, `motorCarrierDetails[${idx}].role.message`, ''),
              error: !!get(errors, `motorCarrierDetails[${idx}].role.message`, ''),
            }}
          />
        </div>
      </div>
    )
  })

  if (isMobile) {
    return (
      <div data-test='carrier-list-wrapper'>
        {availableCarriers.length === 0 && (
          <CarrierListItemEmpty message='There are no Carriers to show.' />
        )}

        {availableCarriers.map(motorCarrierDetail => {
          const canEdit = currentUserCanAdmin(motorCarrierDetail)
          const idx = availableCarriers.indexOf(motorCarrierDetail)
          const hasAccessBeenRemoved =
            !isCarrierChecked(motorCarrierDetail) &&
            initialCarriers.some(
              selected => motorCarrierDetail.motorCarrierId === selected.motorCarrierId
            )
          return (
            <CarrierListItem
              key={motorCarrierDetail.motorCarrierId}
              idx={idx}
              motorCarrierId={motorCarrierDetail.motorCarrierId}
              legalName={motorCarrierDetail.legalName}
              control={control}
              isItemSelected={!!motorCarrierDetail?.checked}
              canEdit={!!canEdit}
              hasAccessBeenRemoved={hasAccessBeenRemoved}
            />
          )
        })}
      </div>
    )
  }

  return (
    <div data-test='carrier-list-wrapper'>
      {carrierErrors && validationBannerElement}
      <div>
        {tableHeadingElements}
        {rowElements}
      </div>
    </div>
  )
}
